import { MantineProvider } from '@mantine/core';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';
import { useMemo } from 'react';
import { type Control, useWatch } from 'react-hook-form';

import type { CurrencyResponse, EstimationModelResponse } from '@/api/schema/data-contracts';
import { DropdownFormField } from '@/components/atoms/DropdownFormField';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { NumericFormField } from '@/components/atoms/NumericFormField';
import { useMessages } from '@/hooks/messages';
import { sortResourceTypes } from '@/utils/sorting';

import { ResourceColumnHeader } from '../../ResourceColumnHeader';
import { resourceCostsMessageDefinitions } from './messages';
import type { CostPerUnitFormModel } from './model';

interface Row {
  id: '';
}

interface CostPerUnitTableProps {
  control: Control<CostPerUnitFormModel>;
  currencies: CurrencyResponse[];
  estimationModel: EstimationModelResponse;
  resourceDisplayNames?: Record<string, string>;
  onBlur: () => void;
  isReadOnly?: boolean;
  onResourceColumnHeaderRename?: (resourceTypeId: string, newName: string) => Promise<void>;
}

export const CostPerUnitTable: FC<CostPerUnitTableProps> = ({
  control,
  currencies,
  estimationModel,
  resourceDisplayNames,
  onBlur,
  isReadOnly,
  onResourceColumnHeaderRename,
}) => {
  const messages = useMessages(resourceCostsMessageDefinitions);
  const formModel = useWatch({ control: control });

  const currencyOptions = useMemo(
    () =>
      currencies
        .filter((currency) => estimationModel.currencyIds?.includes(currency.id))
        .map((currency) => ({
          label: currency.code,
          value: currency.id,
        })),
    [currencies, estimationModel],
  );

  const resourceTypeCostColumns: DataTableColumn<Row>[] = sortResourceTypes(
    estimationModel.resourceTypes,
    estimationModel.resourceTypeGroups,
  ).map((resourceType, index) => ({
    accessor: `resource_type_id_${resourceType.id}`,
    title: (
      <ResourceColumnHeader
        resourceTypeId={resourceType.id}
        displayName={resourceDisplayNames?.[resourceType.id] ?? resourceType.name}
        isReadOnly={!!isReadOnly}
        onRename={onResourceColumnHeaderRename}
      />
    ),
    noWrap: true,
    textAlign: 'right',
    render: () =>
      isReadOnly || resourceType.userModifiable === false ? (
        <FormattedNumber
          value={
            formModel.resourceTypeCosts?.find((x) => x.resourceTypeId === resourceType.id)?.price
          }
        />
      ) : (
        <NumericFormField
          control={control}
          fieldPath={`resourceTypeCosts.${index}.price`}
          onBlur={onBlur}
        />
      ),
  }));

  const columns: DataTableColumn<Row>[] = [
    {
      accessor: 'currency',
      title: messages.currency(),
      noWrap: true,
      render: () =>
        isReadOnly ? (
          currencyOptions.find((x) => x.value === formModel.currencyId)?.label
        ) : (
          <DropdownFormField
            control={control}
            fieldPath='currencyId'
            options={currencyOptions}
            onChange={onBlur}
          />
        ),
    },
    ...resourceTypeCostColumns,
  ];

  // todo create a new component for this table that will be used in other places in detailed estimation tabs
  return (
    <MantineProvider>
      <div className='relative'>
        <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
        <DataTable<Row>
          highlightOnHover
          withTableBorder
          className='table-hover rounded-xl p-2 whitespace-nowrap'
          records={[
            {
              id: '' /* id is needed just to prevent react warning about missing key property */,
            },
          ]}
          columns={columns}
        />
      </div>
    </MantineProvider>
  );
};
